import type { Auth, Token } from '@integration-layer/configs/commercelayer'
import { isTokenValid } from '@integration-layer/configs/commercelayer'
import { StorageSerializers } from '@vueuse/core'

/**
 * Manages customer authentication for Commerce Layer.
 *
 * @function useClCustomerAuth
 * @param {string} email - Customer's email address used for token storage and retrieval.
 *
 * @returns {Auth} An authentication object with organization and token management methods.
 *
 * @description
 * This composable:
 * - Generates a unique storage key based on market and email
 * - Manages in-memory and local storage of authentication tokens
 * - Provides methods to get and refresh customer tokens
 *
 * @example
 * const { organization, getToken, refreshToken } = useClCustomerAuth('user@example.com')
 * const accessToken = await getToken()
 */
export const useClCustomerAuth = (email: string): Auth => {
  const config = useRuntimeConfig()
  const configs = useConfigs()
  const organization = config.public.commercelayer.organization
  const market = configs.value.cl_market_code
  const country = configs.value.country

  const key = `${market}_${email}`

  /**
   * In-memory cache for token promises to prevent multiple simultaneous token requests.
   * @type {Record<string, Promise<Token>>}
   * @private
   */
  const inMemoryTokenPromise: Record<string, Promise<Token>> = {}

  /**
   * Local storage for persisting customer tokens.
   * Only initialized on the client-side.
   *
   * @type {import('@vueuse/core').Ref<Token | null>}
   * @private
   */
  const storage = process.client
    ? useLocalStorage<Token>(`commercelayer:customerToken:${key}`, null, {
        serializer: StorageSerializers.object,
      })
    : null

  /**
   * Retrieves a valid access token, refreshing if necessary.
   *
   * @async
   * @function getToken
   * @returns {Promise<string>} A valid access token.
   */
  const getToken = async () => {
    const tokenPromise = inMemoryTokenPromise[key]
    const token = (await tokenPromise) ?? storage?.value
    if (token && isTokenValid(token)) return token.accessToken

    return await refreshToken()
  }

  /**
   * Refreshes the customer authentication token.
   *
   * @async
   * @function refreshToken
   * @returns {Promise<string>} A new access token.
   *
   * @throws {Error} If token retrieval fails.
   */
  const refreshToken = async () => {
    const tokenPromise = $fetch<Token>(`/api/cl/getCustomerToken`, {
      method: 'POST',
      body: {
        country,
      },
    })
    // @ts-ignore
    inMemoryTokenPromise[key] = tokenPromise
    const newToken = await tokenPromise

    if (storage) {
      // @ts-ignore
      storage.value = newToken
    }

    return newToken.accessToken
  }

  return {
    organization,
    getToken,
    refreshToken,
  }
}
